import {
  I2cInput,
  I2cPhoneField,
  I2cTextarea,
  I2cIcon,
  I2cTooltip,
  I2cRadioGroup,
  I2cRadioButton,
  I2cFileUpload,
  I2cButton,
} from "@webcomponents/i2cwebcomponents/dist/react";
import React, { useRef, useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import "../Prepaid.scss";
import { useMutation } from "react-query";
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import FilesUploadService from "../../../Services/FilesUploadService";
import { emailAdressValidator } from "../../../Utils/stringUtls";
const FinancialIntitution = ({
  type,
  setFormValidStates,
  setAddFormData,
  addFormData,
  formDataJson,
  isFetching,
}) => {
  const Manual = useRef(null);
  // Define validation schema using Zod
  const validationSchema = z.object({
    cp_afi_income_account: z
      .string()
      .min(1, { message: "Income account is required" }),
    cp_afi_funding_account: z
      .string()
      .min(1, { message: "Funding account is required" }),
    cp_afi_operating_account: z
      .string()
      .min(1, { message: "Operating account is required" }),
    cp_afi_inst_name: z
      .string()
      .min(1, { message: "Institution name is required" }),
    cp_afi_routing_number: z
      .string()
      .min(1, { message: "Routing number is required" }),
    cp_afi_p_contact_name: z
      .string()
      .min(1, { message: "Contact name is required" }),
    cp_afi_p_contact_title: z.string().min(1, { message: "Title is required" }),
    cp_afi_physical_address: z
      .string()
      .min(1, { message: "Please fill out this field." }),
    cp_afi_phone_number: z
      .string()
      .min(1, { message: "Phone number is required" }),
    cp_afi_email: z
      .string()
      .min(1, { message: "Email address is required" })
      .refine((val) => emailAdressValidator(val), "Invalid Email address"),
    cp_afi_cpm_pci: z.string().refine((val) => {
      return type !== "credit"
        ? true
        : type === "credit" && val.length
          ? true
          : false;
    }),
  });

  // Initialize React Hook Form with Zod resolver
  const methods = useForm({
    resolver: zodResolver(validationSchema),
    mode: "all",
    reValidateMode: "onChange",
    shouldUseNativeValidation: false,
    criteriaMode: "firstError",
    defaultValues: {
      cp_afi_income_account: "",
      cp_afi_funding_account: "",
      cp_afi_operating_account: "",
      cp_afi_inst_name: "",
      cp_afi_routing_number: "",
      cp_afi_p_contact_name: "",
      cp_afi_p_contact_title: "",
      cp_afi_physical_address: "",
      cp_afi_phone_number: "",
      cp_afi_phone_number_country: "",
      cp_afi_email: "",
      cp_afi_cpm_pci: "",
    },
  });

  const fields = [
    "cp_afi_income_account",
    "cp_afi_funding_account",
    "cp_afi_operating_account",
    "cp_afi_inst_name",
    "cp_afi_routing_number",
    "cp_afi_p_contact_name",
    "cp_afi_p_contact_title",
    "cp_afi_physical_address",
    "cp_afi_phone_number",
    "cp_afi_phone_number_country",
    "cp_afi_email",
    "cp_afi_cpm_pci",
  ];
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        methods.setValue(field, formDataJson[2]["2_1"][field] ?? "");
      });
      methods.trigger();
      handleValueChanges();
    }
  }, [isFetching]);
  const {
    control,
    formState: { errors },
  } = methods;

  // Trigger validation when component mounts
  React.useEffect(() => {
    methods.trigger();
  }, []);
  const formData = new FormData();
  const handleFileUpload = async (name) => {
    try {
      formData.append("file", Manual.current.fileUpload.files[0]);
      await fileUpload.mutate(formData);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const fileUpload = useMutation({
    mutationFn: async (name) => {
      return await FilesUploadService.uploadFile(name);
    },
    onSuccess: (data) => {
      methods.setValue("afi_cpm_compliant_file", data?.data?.url);
      methods.trigger();
      handleValueChanges();
    },
    onError: (e) => {
      console.log("api error");
    },
  });
  const downloadFile = async (fileName) => {
    try {
      await fileDownload.mutate(fileName);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };
  const fileDownload = useMutation({
    mutationFn: async (fileName) => {
      return await FilesUploadService.downloadFile(fileName);
    },
    onSuccess: (data) => {
      const url = window.URL.createObjectURL(new Blob([data.data]));
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";

      // Set the download attribute with the file name
      // link.setAttribute('download', fileName);

      // Append to the document and trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      link.parentNode.removeChild(link);
    },
    onError: (e) => {
      console.log("api error");
    },
  });
  const handleValueChanges = () => {
    const values = methods.getValues();
    if (type !== "credit") {
      delete values.cp_afi_cpm_pci;
    }
    setAddFormData({
      ...addFormData,
      2: {
        ...addFormData["2"],
        "2_1": { ...values },
      },
    });
  };
  // Update form validation state when errors change
  React.useEffect(() => {
    const errorsLength = Object.keys(errors).length;
    if (errorsLength === 0) setFormValidStates("2_1", true);
    else setFormValidStates("2_1", false);
  }, [Object.keys(errors).length]);
  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-column-sm gap-row-md" className="section-space">
          <div i2c-grid="12">
            <div className="sub-heading">Account Information</div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_income_account"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Income Account"
                    required
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Income Account"
                    name={"cp_afi_income_account"}
                    size="large"
                  >
                    <I2cTooltip
                      slot="suffix"
                      hoist
                      content="Income account will receive the payment program's revenue and income. Interchange and card program fees, as well as interest earned by the client will go into this account."
                      placement="right-start"
                    >
                      <I2cIcon name="cs-linear-help-circle"></I2cIcon>
                    </I2cTooltip>
                  </I2cInput>
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_income_account &&
                  methods.formState.touchedFields.cp_afi_income_account
                    ? true
                    : false
                }
                message={
                  errors.cp_afi_income_account?.message ?? "Invalid input"
                }
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_funding_account"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Funding Account"
                    required
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Funding Account"
                    name={"cp_afi_funding_account"}
                    size="large"
                  >
                    <I2cTooltip
                      slot="suffix"
                      hoist
                      content="Funding account will receive payments from cardholders into their card accounts. Credit card payments will go into this account."
                      placement="right-start"
                    >
                      <I2cIcon name="cs-linear-help-circle"></I2cIcon>
                    </I2cTooltip>
                  </I2cInput>
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_funding_account &&
                  methods.formState.touchedFields.cp_afi_funding_account
                    ? true
                    : false
                }
                message={
                  errors.cp_afi_funding_account?.message ?? "Invalid input"
                }
              /> */}
            </div>
          </div>

          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_operating_account"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Operating Account"
                    helpText=""
                    required
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Operating Account"
                    name={"cp_afi_operating_account"}
                    size="large"
                  >
                    <I2cTooltip
                      slot="suffix"
                      hoist
                      content="Operating account contains unsettled funds involving transactions, such as B2C and C2B."
                      placement="right-start"
                    >
                      <I2cIcon name="cs-linear-help-circle"></I2cIcon>
                    </I2cTooltip>
                  </I2cInput>
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_operating_account &&
                  methods.formState.touchedFields.cp_afi_operating_account
                    ? true
                    : false
                }
                message={
                  errors.cp_afi_operating_account?.message ?? "Invalid input"
                }
              /> */}
            </div>
          </div>
        </div>
        <div i2c-grid="grid gap-column-sm gap-row-md">
          <div i2c-grid="12">
            <div className="sub-heading mb-4">Financial Information</div>
            <div className="custom-label-light">
              Provide details in this section if you are working with an
              intermediary, a direct scheme member (BIN Sponsor)
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_inst_name"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    required
                    placeholder="Financial Institution Name"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Financial Institution Name"
                    name={"cp_afi_inst_name"}
                    size="large"
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_inst_name &&
                  methods.formState.touchedFields.cp_afi_inst_name
                    ? true
                    : false
                }
                message={errors.cp_afi_inst_name?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_routing_number"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    required
                    placeholder="Routing Number"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Routing Number"
                    name={"cp_afi_routing_number"}
                    size="large"
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_routing_number &&
                  methods.formState.touchedFields.cp_afi_routing_number
                    ? true
                    : false
                }
                message={
                  errors.cp_afi_routing_number?.message ?? "Invalid input"
                }
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_p_contact_name"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    required
                    className="single-input-field"
                    placeholder="Contact Name"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Contact Name"
                    name={"cp_afi_p_contact_name"}
                    size="large"
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_p_contact_name &&
                  methods.formState.touchedFields.cp_afi_p_contact_name
                    ? true
                    : false
                }
                message={
                  errors.cp_afi_p_contact_name?.message ?? "Invalid input"
                }
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_p_contact_title"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Title"
                    required
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Title"
                    name={"cp_afi_p_contact_title"}
                    size="large"
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_p_contact_title &&
                  methods.formState.touchedFields.cp_afi_p_contact_title
                    ? true
                    : false
                }
                message={
                  errors.cp_afi_p_contact_title?.message ?? "Invalid input"
                }
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <I2cPhoneField
                format="INTERNATIONAL"
                label="Phone Number"
                name="cp_afi_phone_number"
                required
                size="large"
                country-box
                selectedCountry={methods.watch("cp_afi_phone_number_country") || "US"}
                errorMessageVisible={false}
                onI2cChange={(e) => {
                  const country = e.target.country;
                  const isValid = e.target.isValid;
                  const value = e.target.value;
                  methods.setValue("cp_afi_phone_number_country", country);
                  methods.setValue("cp_afi_phone_number", value);
                  handleValueChanges();
                  if (!isValid) {
                    methods.setError("cp_afi_phone_number", {
                      message: e.target.guide,
                    });
                  } else {
                    methods.clearErrors("cp_afi_phone_number");
                  }
                }}
                value={methods.watch("cp_afi_phone_number")}
              />
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_email"
                control={control}
                render={({ field }) => (
                  <I2cInput
                    {...field}
                    className="single-input-field"
                    placeholder="Email"
                    required
                    size="large"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Email"
                    type="email"
                    name={"cp_afi_email"}
                  />
                )}
              />
              {/* <ErrorMessage
                show={
                  errors.cp_afi_email &&
                  methods.formState.touchedFields.cp_afi_email
                    ? true
                    : false
                }
                message={errors.cp_afi_email?.message ?? "Invalid input"}
              /> */}
            </div>
          </div>
          <div i2c-grid="6@md">
            <div className="single-field-wrapper">
              <Controller
                name="cp_afi_physical_address"
                control={control}
                render={({ field }) => (
                  <I2cTextarea
                    {...field}
                    className="single-input-field"
                    required
                    size="large"
                    placeholder="Physical Address"
                    onI2cInput={(e) => {
                      const value = e.target.value;
                      field.onChange(value);
                      handleValueChanges();
                    }}
                    label="Physical Address"
                    name={"cp_afi_physical_address"}
                    countLetters="true"
                    characterLimit="256"
                    rows={2}
                  />
                )}
              />
              <ErrorMessage
                show={
                  errors.cp_afi_physical_address &&
                  methods.formState.touchedFields.cp_afi_physical_address
                    ? true
                    : false
                }
                message={
                  errors.cp_afi_physical_address?.message ?? "Invalid input"
                }
              />
            </div>
          </div>
          {type === "credit" && (
            <>
              <div i2c-grid="12">
                <div className="single-field-wrapper">
                  <div className="custom-label-light mb-16">
                    Is the card program manager PCI compliant? <sup>•</sup>
                  </div>
                  <Controller
                    name="cp_afi_cpm_pci"
                    control={control}
                    render={({ field }) => (
                      <I2cRadioGroup
                        {...field}
                        name="cp_afi_cpm_pci"
                        label=""
                        value={methods.watch("cp_afi_cpm_pci")}
                        onI2cChange={(e) => {
                          field.onChange(e.target.value);
                          handleValueChanges();
                        }}
                      >
                        <I2cRadioButton
                          key="yes"
                          name="cp_afi_cpm_pci"
                          value={"Yes"}
                        >
                          Yes
                        </I2cRadioButton>
                        <I2cRadioButton
                          key="no"
                          name="cp_afi_cpm_pci"
                          value={"No"}
                        >
                          No
                        </I2cRadioButton>
                      </I2cRadioGroup>
                    )}
                  />
                  <ErrorMessage
                    show={
                      errors.cp_pm_sign &&
                      methods.formState.touchedFields.cp_pm_sign
                        ? true
                        : false
                    }
                    message={errors.cp_pm_sign?.message ?? "Invalid input"}
                  />
                </div>
              </div>
              {methods.watch("cp_afi_cpm_pci") === "Yes" && (
                <>
                  <div i2c-grid="12">
                    <div className="custom-label-light mb-16">
                      Is the card program manager PCI compliant?
                    </div>
                    <I2cFileUpload
                      ref={Manual}
                      autoProcessQueue={false}
                      no-mobile-controls
                      max-files={1}
                      className="customFileUpload"
                      setAcceptedFiles=".pdf"
                      icon="linear-document-upload"
                      label="Click to upload"
                      caption="or drag and drop"
                      name="afi_cpm_compliant_file"
                      message="Only PDF file (max. 5MB)."
                      onI2cFileRemoved={(e) => {
                        methods.setValue("afi_cpm_compliant_file", "");
                        methods.trigger();
                        handleValueChanges();
                      }}
                      onI2cAddedFile={(e) => {
                        handleFileUpload(
                          Manual.current.getDropZone().files[
                            Manual.current.getDropZone().files.length - 1
                          ],
                        );
                      }}
                    />

                    {Object.keys(formDataJson ?? {}).length ? (
                      <>
                        {formDataJson[2]["2_1"]?.afi_cpm_compliant_file !=
                          null && (
                          <div className="mt-16">
                            <I2cButton
                              size="small"
                              variant="link color"
                              className=""
                              onClick={() => {
                                downloadFile(
                                  formDataJson[2]["2_1"]
                                    ?.afi_cpm_compliant_file,
                                );
                              }}
                            >
                              <I2cIcon
                                slot="prefix"
                                name="linear-document-download"
                                label="add"
                              ></I2cIcon>{" "}
                              Download File
                            </I2cButton>
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default FinancialIntitution;
